export const DEFAULT_PROPOSAL_PORTRAIT =
  "https://book.raykschoerling.com/rayk-portrait-cutout-v5-email.jpg?v=20260903b";

type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

export function defaultProposalExpiry(days = 14): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(23, 59, 0, 0);
  return toLocalInputValue(date);
}

export function localDateTimeToIso(value: string, timeZone = "Europe/Berlin"): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) throw new Error("Bitte Datum und Uhrzeit vollständig auswählen.");

  const [, yearText, monthText, dayText, hourText, minuteText] = match;
  const parts = {
    year: Number(yearText),
    month: Number(monthText),
    day: Number(dayText),
    hour: Number(hourText),
    minute: Number(minuteText),
  };
  const desiredUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  let candidate = desiredUtc - offsetAt(desiredUtc, timeZone);
  candidate = desiredUtc - offsetAt(candidate, timeZone);

  const actual = zonedParts(candidate, timeZone);
  if (
    actual.year !== parts.year ||
    actual.month !== parts.month ||
    actual.day !== parts.day ||
    actual.hour !== parts.hour ||
    actual.minute !== parts.minute
  ) {
    throw new Error(
      "Diese Uhrzeit existiert wegen der Zeitumstellung nicht. Bitte eine andere Zeit wählen.",
    );
  }

  return new Date(candidate).toISOString();
}

export function proposalInvitationMailto(input: {
  recipientEmail: string;
  recipientName: string;
  link: string;
  offerTitle: string;
}): string {
  const firstName = input.recipientName.trim().split(/\s+/)[0] || input.recipientName.trim();
  const subject = `Deine persönliche Einladung · ${input.offerTitle}`;
  const body = [
    `Hallo ${firstName},`,
    "",
    "ich habe dir eine persönliche Einladung mit ausgewählten Zeiten vorbereitet.",
    "",
    input.link,
    "",
    "Wenn es passt, kannst du dort verbindlich reservieren.",
    "",
    "Herzlich",
    "Rayk",
  ].join("\n");

  return `mailto:${encodeURIComponent(input.recipientEmail.trim())}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function toLocalInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function offsetAt(timestamp: number, timeZone: string): number {
  const parts = zonedParts(timestamp, timeZone);
  const representedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
  );
  const roundedTimestamp = Math.floor(timestamp / 60_000) * 60_000;
  return representedAsUtc - roundedTimestamp;
}

function zonedParts(timestamp: number, timeZone: string): ZonedDateParts {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const values: Partial<ZonedDateParts> = {};
  for (const part of formatter.formatToParts(new Date(timestamp))) {
    if (
      part.type === "year" ||
      part.type === "month" ||
      part.type === "day" ||
      part.type === "hour" ||
      part.type === "minute"
    ) {
      values[part.type] = Number(part.value);
    }
  }

  if (
    values.year === undefined ||
    values.month === undefined ||
    values.day === undefined ||
    values.hour === undefined ||
    values.minute === undefined
  ) {
    throw new Error("Die ausgewählte Zeitzone konnte nicht verarbeitet werden.");
  }

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
  };
}
